import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TrainingCard, TrainingCardRef } from '@/components/calendar/TrainingCard';
import { AddTrainingModal } from '@/components/calendar/AddTrainingModal';
import { AddClassModal } from '@/components/calendar/AddClassModal';
import { DeleteClassModal } from '@/components/calendar/DeleteClassModal';
import { TrainingNotesModal } from '@/components/calendar/TrainingNotesModal';
import { TrainingViewModal } from '@/components/calendar/TrainingViewModal';
import { DailyView } from '@/components/calendar/DailyView';

import { TrainingFilterModal, TrainingFilters } from '@/components/calendar/TrainingFilterModal';
import { MonthSlider } from '@/components/calendar/MonthSlider';
import { useTrainingsWithNotes, useClassesWithNotes, useMonthlyTotals, useMonthlyClasses, useMonthlyTrainings, useUpdateTrainingStatus, useUpdateTrainingNotes } from '@/hooks/useTrainings';
import { useInstructorClasses } from '@/hooks/useInstructorClasses';
import { useInstructor } from '@/hooks/useInstructor';
import { format, addMonths, subMonths, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval, differenceInDays } from 'date-fns';
import { Training } from '@/types/trainings';
import { ArrowLeft, ArrowRight, Plus, Filter } from 'lucide-react';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/contexts/I18nContext';
import { useLocation } from 'react-router-dom';

export default function Calendar() {
  const { user, loading } = useAuth();
  const { isInstructor, isLoading: instructorLoading } = useInstructor();
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState<'classes' | 'trainings'>('classes');
  const [isAddTrainingModalOpen, setIsAddTrainingModalOpen] = useState(false);
  const [isAddClassModalOpen, setIsAddClassModalOpen] = useState(false);
  const [isEditClassModalOpen, setIsEditClassModalOpen] = useState(false);
  const [selectedClassForEdit, setSelectedClassForEdit] = useState<Training | null>(null);
  const [isDeleteClassModalOpen, setIsDeleteClassModalOpen] = useState(false);
  const [selectedClassForDelete, setSelectedClassForDelete] = useState<Training | null>(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedTrainingForNotes, setSelectedTrainingForNotes] = useState<Training | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedTrainingForView, setSelectedTrainingForView] = useState<Training | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTrainingForEdit, setSelectedTrainingForEdit] = useState<Training | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [showFixedButton, setShowFixedButton] = useState(false);
  const [lastUpdatedTrainingId, setLastUpdatedTrainingId] = useState<string | null>(null);
  const [lastCreatedClassId, setLastCreatedClassId] = useState<string | null>(null);
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const [updatingNotesTrainingId, setUpdatingNotesTrainingId] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<TrainingFilters>({
    types: [],
    modalities: [],
    startDate: null,
    endDate: null,
    instructor: null,
    statuses: [],
    techniques: [],
    categories: []
  });
  const currentDayRef = useRef<HTMLDivElement>(null);
  const nearestTrainingRef = useRef<HTMLDivElement>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);
  const monthNavigationRef = useRef<HTMLDivElement>(null);
  const trainingCardRefs = useRef<Map<string, TrainingCardRef>>(new Map());
  const viewModalRef = useRef<{ forceReload: () => Promise<void>; forceReloadInstructor: () => Promise<void> } | null>(null);
  const editModalRef = useRef<{ forceReload: () => Promise<void> } | null>(null);
  const [viewModalRefreshKey, setViewModalRefreshKey] = useState(0);

  const selectedMonth = selectedDate.getMonth() + 1;
  const selectedYear = selectedDate.getFullYear();

  const queryClient = useQueryClient();
  const { t } = useI18n();

  // Set default tab based on user type
  useEffect(() => {
    if (!instructorLoading) {
      setActiveTab(isInstructor ? 'classes' : 'trainings');
    }
  }, [isInstructor, instructorLoading]);

  // Check for navigation state to open Add Class modal
  useEffect(() => {
    if (location.state?.openAddClassModal && isInstructor) {
      setIsAddClassModalOpen(true);
      setActiveTab('classes');
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, isInstructor]);

  // Check for navigation state to open Add Training modal
  useEffect(() => {
    if (location.state?.openAddTrainingModal) {
      setIsAddTrainingModalOpen(true);
      setActiveTab('trainings');
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Use different hooks based on active tab and user type
  const { data: trainingsData, isLoading: trainingsLoading, refetch: refetchTrainings } = useTrainingsWithNotes({
    month: selectedMonth,
    year: selectedYear
  });

  // For instructor classes tab, use instructor classes hook - same as DailyView
  // Use today's date to get all future classes, not the selected month date
  const today = useMemo(() => new Date(), []); // Stabilize the date to prevent unnecessary re-renders
  const { classes: instructorClassesData, isLoading: instructorClassesLoading, refetch: refetchInstructorClasses } = useInstructorClasses(today);

  // Get classes notes data for instructor classes tab
  const { data: classesNotesData } = useClassesWithNotes({
    month: selectedMonth,
    year: selectedYear,
  });

  // Determine which data to use based on active tab
  const isClassesTab = isInstructor && activeTab === 'classes';
  const trainings = isClassesTab ? (instructorClassesData || []) : (trainingsData?.trainings || []);

  // Use appropriate notes data based on active tab
  const notesData = isClassesTab ? (classesNotesData?.notesData || {}) : (trainingsData?.notesData || {});





  // Refetch data when month/year changes
  useEffect(() => {
    if (isClassesTab) {
      refetchInstructorClasses();
    } else {
      refetchTrainings();
    }
  }, [selectedMonth, selectedYear, isClassesTab]); // Removidas as funções refetch das dependências

  const hasActiveFilters = () => {
    return (
      activeFilters.types.length > 0 ||
      activeFilters.modalities.length > 0 ||
      activeFilters.startDate !== null ||
      activeFilters.endDate !== null ||
      activeFilters.instructor !== null ||
      activeFilters.statuses.length > 0 ||
      activeFilters.techniques.length > 0 ||
      activeFilters.categories.length > 0
    );
  };

  // Filter trainings based on active filters

  
  // Simplified filtering - show all trainings for now
  const filteredTrainings = useMemo(() => {
    return trainings.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  }, [trainings]);
  

  

  


  const { data: monthlyTotals = [] } = useMonthlyTotals(selectedYear);
  const { data: monthlyClasses = [] } = useMonthlyClasses(selectedYear);
  const { data: monthlyTrainings = [] } = useMonthlyTrainings(selectedYear);
  
  // Use the appropriate monthly data based on active tab
  const monthlyData = isClassesTab ? monthlyClasses : monthlyTrainings;

  const updateStatusMutation = useUpdateTrainingStatus();
  const updateNotesMutation = useUpdateTrainingNotes();

  // Redirect if not authenticated
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#160C0C] text-white">
        <div className="text-lg font-medium">Loading calendar...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const handleMonthChange = (direction: 'next' | 'prev') => {
    setSelectedDate(prev => 
      direction === 'next' ? addMonths(prev, 1) : subMonths(prev, 1)
    );
  };

  const handleStatusUpdate = async (trainingId: string, status: string) => {
    try {
      // Update training status
      const { error } = await supabase
        .from('trainings')
        .update({ status })
        .eq('id', trainingId)
        .eq('user_id', user?.id);

      if (error) throw error;

      // Invalidate and refetch data
      await queryClient.invalidateQueries({ queryKey: ['trainings'] });
      await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      await queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
      await queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      await queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });

      // Refetch instructor classes if user is instructor
      if (isInstructor) {
        await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
        await queryClient.invalidateQueries({ queryKey: ['instructor-classes', user?.id] });
      }

      // Show success message
      console.log(`Training status updated to ${status}`);
    } catch (error) {
      console.error('Error updating training status:', error);
      console.log('Failed to update training status');
    }
  };

  const handleNotesUpdate = async (trainingId: string, notes: string) => {
    // If notes is empty string, it means we want to open the notes modal
    if (notes === '') {
      const training = trainings.find(t => t.id === trainingId);
      if (training) {
        handleOpenNotesModal(training);
      }
      return;
    }
    
    setUpdatingNotesTrainingId(trainingId);
    try {
      await updateNotesMutation.mutateAsync({ training_id: trainingId, notes });
      
      // Force refresh of all data to update the UI
      await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      const result = await refetchTrainings();
      
      // Force a re-render of all TrainingCard components to update their notes status
      setNotesRefreshKey(prev => prev + 1);
      
    } catch (error) {
      console.error('Error updating training notes:', error);
      throw error;
    } finally {
      setUpdatingNotesTrainingId(null);
    }
  };

  const handleTrainingCreated = async () => {
    // Force invalidate and refetch trainings to get the new training
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings'] });
    await queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
    await queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
    await queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
    
    // Also invalidate the specific query key with current month/year
    await queryClient.invalidateQueries({ 
      queryKey: ['trainings-with-notes', user?.id, selectedMonth, selectedYear] 
    });
    
    // Refetch the current data
    await refetchTrainings();
    
    // Close the modal
    setIsAddTrainingModalOpen(false);
    
    // Show success message
    console.log('Training created successfully');
  };

  const handleOpenNotesModal = (training: Training) => {
    setSelectedTrainingForNotes(training);
    setIsNotesModalOpen(true);
  };

  const handleOpenViewModal = (training: Training) => {
    setSelectedTrainingForView(training);
    setIsViewModalOpen(true);
  };

  const handleOpenEditModal = (training: Training) => {
    setSelectedTrainingForEdit(training);
    setIsEditModalOpen(true);
  };

  const handleTrainingUpdated = async () => {
    // Force invalidate and refetch trainings to get updated data
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
    const result = await refetchTrainings();
    
    // Check if result.data exists and has the correct structure
    if (selectedTrainingForView && result.data && result.data.trainings) {
      const updatedTraining = result.data.trainings.find(t => t.id === selectedTrainingForView.id);
      
      if (updatedTraining) {
        // Force a complete re-render by creating a new object
        setSelectedTrainingForView({ ...updatedTraining });
        
        // Force reload of instructor and partners data in the view modal
        if (viewModalRef.current) {
          await viewModalRef.current.forceReload();
          await viewModalRef.current.forceReloadInstructor();
        }
        
        // Force refresh by updating the key
        setViewModalRefreshKey(prev => prev + 1);
        
        // Force multiple updates to ensure the modal refreshes
        setTimeout(() => {
          setSelectedTrainingForView(prev => prev ? { ...prev } : prev);
        }, 100);
        
        setTimeout(() => {
          setSelectedTrainingForView(prev => prev ? { ...prev } : prev);
        }, 500);
        
        // Force a complete modal refresh by closing and reopening
        setTimeout(() => {
          setIsViewModalOpen(false);
          setTimeout(() => {
            setIsViewModalOpen(true);
          }, 50);
        }, 300);
      }
    }

    // Update the selected training in the edit modal if it exists
    if (selectedTrainingForEdit && result.data && result.data.trainings) {
      const updatedTraining = result.data.trainings.find(t => t.id === selectedTrainingForEdit.id);
      
      if (updatedTraining) {
        // Force a complete re-render by creating a new object
        setSelectedTrainingForEdit({ ...updatedTraining });
        
        // Force multiple updates to ensure the modal refreshes immediately
        setTimeout(() => {
          setSelectedTrainingForEdit(prev => prev ? { ...prev } : prev);
        }, 50);
        
        setTimeout(() => {
          setSelectedTrainingForEdit(prev => prev ? { ...prev } : prev);
        }, 200);
      }
    }
  };

  const handleTrainingDeleted = () => {
    // Invalidate and refetch trainings to remove deleted training from the list
    queryClient.invalidateQueries({ queryKey: ['trainings'] });
  };

  const handleNotesSaved = async (trainingId: string, notes: string) => {
    // If the view modal is open and showing the same training, force a re-render
    if (selectedTrainingForView && selectedTrainingForView.id === trainingId) {
      // Force a re-render by updating the selectedTrainingForView with a new object
      // This will trigger the useEffect in TrainingViewModal and refetch notes
      setSelectedTrainingForView({ ...selectedTrainingForView });
    }
  };

  const handleDataSaved = async () => {
    // Invalidate appropriate queries based on active tab
    if (isClassesTab) {
      await queryClient.invalidateQueries({ queryKey: ['classes-with-notes'] });
      await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
      await queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
    } else {
      await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      const result = await refetchTrainings();
      
      // If the view modal is open, update it with fresh data
      if (selectedTrainingForView && result.data && result.data.trainings) {
        // Find the updated training in the fresh data
        const updatedTraining = result.data.trainings.find(t => t.id === selectedTrainingForView.id);
        if (updatedTraining) {
          setSelectedTrainingForView({ ...updatedTraining });
          
          // Force reload of the view modal
          if (viewModalRef.current) {
            await viewModalRef.current.forceReload();
          }
          
          // Force refresh of partners by updating the refresh key
          setViewModalRefreshKey(prev => prev + 1);
        }
      }
    }
  
  // Force a re-render of all TrainingCard components to update their notes status
  setNotesRefreshKey(prev => prev + 1);
  };

  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = (filters: TrainingFilters) => {
    setActiveFilters(filters);
  };

  const handleClearFilters = () => {
    const emptyFilters: TrainingFilters = {
      types: [],
      modalities: [],
      startDate: null,
      endDate: null,
      instructor: null,
      statuses: [],
      techniques: [],
      categories: []
    };
    setActiveFilters(emptyFilters);
  };

  // Instructor-specific handlers
  const handleAddClass = () => {
    setIsAddClassModalOpen(true);
  };

  const handleConfigureAgenda = () => {
    // TODO: Implement agenda configuration
    console.log('Configure agenda clicked');
  };

  const handleTabChange = (tab: 'classes' | 'trainings') => {
    setActiveTab(tab);
  };

  const handleClassCreated = async () => {
    // Force invalidate and refetch instructor classes to get the new class
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes', user?.id] });
    await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
    await queryClient.invalidateQueries({ queryKey: ['instructor-highlights'] });
    
    // Also invalidate regular trainings since classes are stored in the same table
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings'] });
    await queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
    
    // Refetch the current data
    const result = await refetchTrainings();
    
    // Find the newly created class and scroll to it
    if (result.data && result.data.trainings) {
      // Find the most recently created class (assuming it's the last one in the array)
      const newClass = result.data.trainings[result.data.trainings.length - 1];
      if (newClass) {
        setLastCreatedClassId(newClass.id);
        
        // Trigger scroll after a delay to allow the UI to update
        setTimeout(() => {
          const cardRef = trainingCardRefs.current.get(newClass.id);
          if (cardRef) {
            cardRef.animateUpdate();
            cardRef.scrollIntoView();
          }
        }, 300);
      }
    }
    
    // Close the modal
    setIsAddClassModalOpen(false);
    
    // Show success message
    console.log('Class created successfully');
  };

  const handleOpenEditClassModal = (training: Training) => {
    setSelectedClassForEdit(training);
    setIsEditClassModalOpen(true);
  };

  const handleClassUpdated = async () => {
    // Force invalidate and refetch instructor classes to get updated data
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
    
    // Also invalidate regular trainings since classes are stored in the same table
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
    const result = await refetchTrainings();
    
    // Update the selected class in the edit modal if it exists
    if (selectedClassForEdit && result.data && result.data.trainings) {
      const updatedClass = result.data.trainings.find(t => t.id === selectedClassForEdit.id);
      
      if (updatedClass) {
        // Force a complete re-render by creating a new object
        setSelectedClassForEdit({ ...updatedClass });
        
        // Force multiple updates to ensure the modal refreshes immediately
        setTimeout(() => {
          setSelectedClassForEdit(prev => prev ? { ...prev } : prev);
        }, 50);
        
        setTimeout(() => {
          setSelectedClassForEdit(prev => prev ? { ...prev } : prev);
        }, 200);
      }
    }
  };

  const handleClassDeleted = async (classId?: string) => {
    // If a specific class ID is provided, delete it
    if (classId) {
      try {
        const { error } = await supabase
          .from('trainings')
          .delete()
          .eq('id', classId);

        if (error) {
          throw error;
        }
      } catch (error) {
        console.error('Error deleting class:', error);
        return;
      }
    }

    // Invalidate and refetch classes to remove deleted class from the list
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
  };

  const handleOpenDeleteClassModal = (training: Training) => {
    setSelectedClassForDelete(training);
    setIsDeleteClassModalOpen(true);
  };

  const handleConfirmDeleteClass = async () => {
    if (!selectedClassForDelete) return;

    try {
      // Delete the class
      const { error } = await supabase
        .from('trainings')
        .delete()
        .eq('id', selectedClassForDelete.id);

      if (error) {
        throw error;
      }

      // Invalidate and refetch data
      await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
      await queryClient.invalidateQueries({ queryKey: ['trainings'] });
      await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });

      // Close modal and reset state
      setIsDeleteClassModalOpen(false);
      setSelectedClassForDelete(null);

      // Show success message
      console.log('Class deleted successfully');
    } catch (error) {
      console.error('Error deleting class:', error);
    }
  };

  // Handle scroll to show/hide fixed button and month navigation
  useEffect(() => {
    const handleScroll = () => {
      if (actionBarRef.current && monthNavigationRef.current) {
        const actionBarRect = actionBarRef.current.getBoundingClientRect();
        const monthNavigationRect = monthNavigationRef.current.getBoundingClientRect();
        
        // Show fixed elements when either the action bar or month navigation is not visible
        const isActionBarVisible = actionBarRect.top > 0;
        const isMonthNavigationVisible = monthNavigationRect.top > 0;
        
        setShowFixedButton(!isActionBarVisible || !isMonthNavigationVisible);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Group trainings by day
  const trainingsByDay = useMemo(() => {
    const grouped = filteredTrainings.reduce((acc, training) => {
      const day = format(new Date(training.start_time), 'yyyy-MM-dd');
      if (!acc[day]) {
        acc[day] = [];
      }
      acc[day].push(training);
      return acc;
    }, {} as Record<string, typeof trainings>);

    // Sort trainings by start_time within each day
    Object.keys(grouped).forEach(day => {
      grouped[day].sort((a, b) => 
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      );
    });

    return grouped;
  }, [filteredTrainings]);

  // Get all days in the selected month
  const daysInMonth = useMemo(() => {
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    return eachDayOfInterval({ start: monthStart, end: monthEnd });
  }, [selectedDate]);



  // Find the nearest training to today
  const findNearestTraining = useCallback(() => {
    const today = new Date();
    let nearestTraining = null;
    let minDaysDiff = Infinity;

    filteredTrainings.forEach(training => {
      const trainingDate = new Date(training.start_time);
      const daysDiff = Math.abs(differenceInDays(trainingDate, today));
      
      if (daysDiff < minDaysDiff) {
        minDaysDiff = daysDiff;
        nearestTraining = training;
      }
    });

    return nearestTraining;
  }, [filteredTrainings]);

  // Auto-scroll to current day on mount (only when month changes, not when trainings update)
  useEffect(() => {
    if (currentDayRef.current && selectedMonth === new Date().getMonth() + 1 && selectedYear === new Date().getFullYear()) {
      setTimeout(() => {
        currentDayRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100);
    }
  }, [selectedMonth, selectedYear]); // Removed 'trainings' dependency

  // Removed auto-scroll to nearest training to avoid conflicts with status update scroll

  const isCurrentDay = useCallback((date: Date) => {
    return isSameDay(date, new Date());
  }, []);

  const nearestTraining = useMemo(() => findNearestTraining(), [findNearestTraining]);

  // Debug notesData for selected training
  useEffect(() => {
    if (selectedTrainingForNotes && notesData) {
      const trainingNotes = notesData[selectedTrainingForNotes.id];
      // Debug data removed for cleaner console
    }
  }, [selectedTrainingForNotes, notesData]);

  return (
    <div className="min-h-screen w-screen pb-20" style={{ background: '#160C0C' }}>
      <TopNavigation variant="subtle" />
      
      {/* Instructor Tabs */}
      {isInstructor && (
        <div className="pt-24 px-5">
          <div className="flex border-b-2 border-white/20 mb-4">
            <button
              onClick={() => handleTabChange('classes')}
              className={`flex-1 pb-2 px-1 text-xs font-semibold ${
                activeTab === 'classes' 
                  ? 'text-white border-b-2 border-[#D72E16]' 
                  : 'text-white/60 border-b-2 border-transparent'
              }`}
            >
              {t('calendar.my_classes')}
            </button>
            <button
              onClick={() => handleTabChange('trainings')}
              className={`flex-1 pb-2 px-1 text-xs font-semibold ${
                activeTab === 'trainings' 
                  ? 'text-white border-b-2 border-[#D72E16]' 
                  : 'text-white/60 border-b-2 border-transparent'
              }`}
            >
              {t('calendar.my_trainings')}
            </button>
          </div>
        </div>
      )}
      
      {/* Fixed Action Bar with Month Navigation */}
      <div 
        className={`fixed top-20 left-0 right-0 z-40 px-5 transition-all duration-300 ${
          showFixedButton 
            ? 'translate-y-0 opacity-100' 
            : '-translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="max-w-md mx-auto space-y-3">
          {/* Month Navigation */}
          <div className="flex items-center justify-between bg-[#281B1B]/90 backdrop-blur-sm rounded-lg p-3 border border-white/20">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleMonthChange('prev')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h2 className="text-sm font-semibold text-white">
              {format(selectedDate, 'MMMM yyyy')}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleMonthChange('next')}
              className="text-white hover:bg-white/10"
            >
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Add Training Button */}
          <Button 
            variant="outline"
            onClick={() => setIsAddTrainingModalOpen(true)}
            className="w-full border-white text-white hover:bg-white/10 rounded-full font-semibold bg-[#281B1B]/90 backdrop-blur-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('calendar.add_training')}
          </Button>
        </div>
      </div>
      
      <main className={`${isInstructor ? 'pt-0' : 'pt-16'} px-5`}>
        <div className="py-6">
          {/* Conditional Content based on Tab */}
          {isInstructor && activeTab === 'classes' ? (
            // Instructor Daily View
            <DailyView
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              trainings={[]} // Not used in DailyView anymore
              onTrainingClick={() => {}} // Not used in DailyView - classes use ClassViewModal
              onAddClass={handleAddClass}
              onConfigureAgenda={handleConfigureAgenda}
              onEditClass={handleOpenEditClassModal}
              onClassDeleted={handleClassDeleted}
            />
          ) : (
            // Regular Training View (for non-instructors or instructors on 'trainings' tab)
            <>
              {/* Action Bar */}
              <div ref={actionBarRef} className="flex items-center gap-3 mb-6">
                <Button 
                  variant="outline" 
                  onClick={() => setIsAddTrainingModalOpen(true)}
                  className="flex-1 border-white text-white hover:bg-white/10 rounded-full font-semibold bg-transparent"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {isInstructor && activeTab === 'classes' ? t('calendar.add_class') : t('calendar.add_training')}
                </Button>
                <button 
                  onClick={handleOpenFilterModal}
                  className={`flex items-center justify-center p-2 rounded-md transition-colors ${
                    hasActiveFilters()
                      ? 'text-white bg-white/10'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  {hasActiveFilters() && <span className="ml-0.5 text-xs font-bold">•</span>}
                </button>
              </div>

              {/* Month Slider */}
              {!instructorLoading && monthlyData && monthlyData.length > 0 && (
                <MonthSlider
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onMonthSelect={(month) => {
                    // month é 1-indexed, mas setDate espera 0-indexed
                    setSelectedDate(new Date(selectedYear, month - 1, 1));
                  }}
                  monthlyTotals={monthlyData}
                />
              )}

              {/* Monthly Navigation */}
              <div ref={monthNavigationRef} className="flex items-center justify-between mb-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMonthChange('prev')}
                  className="text-white hover:bg-white/10"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <h2 className="text-lg font-semibold text-white">
                  {format(selectedDate, 'MMMM yyyy')}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMonthChange('next')}
                  className="text-white hover:bg-white/10"
                >
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>

              {/* Filter Status */}
              {hasActiveFilters() && (
                <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white">
                      Showing {filteredTrainings.length} of {trainings.length} trainings
                    </span>
                    <button 
                      onClick={handleClearFilters}
                      className="text-white hover:text-white/80 text-xs font-medium underline"
                    >
                      Clear filters
                    </button>
                  </div>
                </div>
              )}

              {/* Content */}

              {(trainingsLoading || (isClassesTab && instructorClassesLoading)) ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-lg text-white">{t('loading.trainings')}</div>
                </div>
              ) : filteredTrainings.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
                  {/* Empty State Icon */}
                  <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                    <svg 
                      className="w-10 h-10 text-white/40" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={1.5} 
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
                      />
                    </svg>
                  </div>
                  
                  {/* Empty State Title */}
                  <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                    {isInstructor && activeTab === 'classes' ? t('calendar.start_teaching_add_first_class') : t('calendar.no_classes_scheduled_today')}
                  </h3>
                  
                  {/* Empty State Subtitle */}
                  <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                    {isInstructor && activeTab === 'classes' ? t('calendar.schedule_sessions_help_students') : t('calendar.start_teaching_add_first_class')}
                  </p>
                  
                  {/* Call to Action Button */}
                  <Button 
                    variant="outline"
                    onClick={() => isInstructor && activeTab === 'classes' ? setIsAddClassModalOpen(true) : setIsAddTrainingModalOpen(true)}
                    className="mt-8 border-white text-white hover:bg-white/10 rounded-full font-semibold bg-transparent px-8 py-3"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {isInstructor && activeTab === 'classes' ? t('calendar.start_teaching_add_first_class') : t('calendar.add_training')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredTrainings.length > 0 ? (
                    <div className="space-y-6">
                      {filteredTrainings.map((training) => {
                        const trainingDate = new Date(training.start_time);
                        const dayString = format(trainingDate, 'yyyy-MM-dd');
                        const isCurrent = isCurrentDay(trainingDate);
                        
                        return (
                          <div 
                            key={training.id} 
                            ref={isCurrent ? currentDayRef : null}
                            className={`${isCurrent ? 'ring-2 ring-[#D72E16] rounded-lg p-2' : ''}`}
                          >
                            <div className="flex gap-2">
                              {/* Date Column */}
                              <div className="flex-shrink-0 w-12 text-center">
                                <div className={`text-sm font-semibold ${
                                  isCurrent ? 'text-[#D72E16]' : 'text-white'
                                }`}>
                                  {format(trainingDate, 'EEE')}
                                </div>
                                <div className={`text-lg font-semibold ${
                                  isCurrent ? 'text-[#D72E16]' : 'text-white'
                                }`}>
                                  {format(trainingDate, 'dd')}
                                </div>
                              </div>
                              
                              {/* Training Card */}
                              <div className="flex-1">
                                <TrainingCard
                                  ref={(ref) => {
                                    if (ref) {
                                      trainingCardRefs.current.set(training.id, ref);
                                    } else {
                                      trainingCardRefs.current.delete(training.id);
                                    }
                                  }}
                                  training={training}
                                  notesData={notesData}
                                  onStatusUpdate={handleStatusUpdate}
                                  onNotesUpdate={handleNotesUpdate}
                                  isCurrentDay={isCurrent}
                                  onViewTraining={handleOpenViewModal}
                                  notesRefreshKey={notesRefreshKey}
                                  isUpdatingNotes={updatingNotesTrainingId === training.id}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-white/60 py-8">
                      {t('calendar.no_classes_scheduled_today')}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      
      <BottomNavigation />

      {/* Add/Edit Training Modal (only for non-instructor or trainings tab) */}
      <AddTrainingModal
        isOpen={(isAddTrainingModalOpen || isEditModalOpen) && (!isInstructor || activeTab === 'trainings')}
        onClose={() => {
          setIsAddTrainingModalOpen(false);
          setIsEditModalOpen(false);
          setSelectedTrainingForEdit(null);
        }}
        onTrainingCreated={handleTrainingCreated}
        selectedDate={selectedDate}
        onOpenNotesModal={handleOpenNotesModal}
        mode={isEditModalOpen ? 'edit' : 'create'}
        trainingToEdit={selectedTrainingForEdit}
        onTrainingUpdated={handleTrainingUpdated}
      />

      {/* Add/Edit Class Modal */}
      <AddClassModal
        isOpen={(isAddClassModalOpen || isEditClassModalOpen) && (isInstructor && activeTab === 'classes')}
        onClose={() => {
          setIsAddClassModalOpen(false);
          setIsEditClassModalOpen(false);
          setSelectedClassForEdit(null);
        }}
        onClassCreated={handleClassCreated}
        mode={isEditClassModalOpen ? 'edit' : 'create'}
        classToEdit={selectedClassForEdit}
        onClassUpdated={handleClassUpdated}
      />

      {/* Training Notes Modal */}
      <TrainingNotesModal
        isOpen={isNotesModalOpen}
        onClose={() => {
          setIsNotesModalOpen(false);
          setSelectedTrainingForNotes(null);
          setUpdatingNotesTrainingId(null); // Reset loading state when modal is closed
        }}
        training={selectedTrainingForNotes}
        notesData={notesData}
        onSave={handleNotesSaved}
        onDataSaved={handleDataSaved}
      />

      {/* Training View Modal */}
      {selectedTrainingForView && (
        <TrainingViewModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          training={selectedTrainingForView}
          notesData={notesData}
          onStatusUpdate={handleStatusUpdate}
          onOpenNotesModal={handleOpenNotesModal}
          onEditTraining={handleOpenEditModal}
          onTrainingDeleted={handleTrainingDeleted}
          onTrainingUpdated={handleTrainingUpdated}
          refreshKey={viewModalRefreshKey}
          ref={viewModalRef}
        />
      )}

      {/* Delete Class Modal */}
      <DeleteClassModal
        isOpen={isDeleteClassModalOpen}
        onClose={() => {
          setIsDeleteClassModalOpen(false);
          setSelectedClassForDelete(null);
        }}
        onConfirm={handleConfirmDeleteClass}
      />

      {/* Training Filter Modal */}
      <TrainingFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        currentFilters={activeFilters}
        hasActiveFilters={hasActiveFilters()}
      />
    </div>
  );
} 